import React, { useState, useEffect, useContext } from "react";
import {
  Modal,
  Row,
  Col,
  Card,
  Badge,
  Spinner,
  Alert,
  Button,
  Form,
  InputGroup,
  Tab,
  Tabs,
} from "react-bootstrap";
import {
  IconRocket,
  IconBuilding,
  IconUsers,
  IconCash,
  IconCreditCard,
  IconHeadset,
  IconBolt,
  IconPlayerPause,
  IconX,
  IconCheck,
  IconClock,
  IconCalendar,
  IconPlus,
  IconRefresh,
} from "@tabler/icons-react";
import Swal from "sweetalert2";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import UserContext from "../../../contexts/UserContext";
import {
  getAgentDeployment,
  activateAgentDeployment,
  suspendAgentDeployment,
  cancelAgentDeployment,
  markSetupFeePaid,
  processBilling,
  getBillingSummary,
  extendSupport,
  formatDeploymentData,
  canPerformAction,
  formatCurrency,
} from "../services/AgentDeploymentServices";

const AgentDeploymentModal = ({ show, onHide, deploymentId, onSuccess }) => {
  const { user, isOrganization } = useContext(UserContext);

  // Estados principais
  const [loading, setLoading] = useState(false);
  const [deployment, setDeployment] = useState(null);
  const [billingSummary, setBillingSummary] = useState(null);
  const [activeTab, setActiveTab] = useState("general");

  // Estados de ações
  const [actionLoading, setActionLoading] = useState({});
  const [billingLoading, setBillingLoading] = useState(false);
  const [supportLoading, setSupportLoading] = useState(false);

  // Estados de formulários
  const [supportExtensionData, setSupportExtensionData] = useState({
    months: 1,
    reason: "",
  });

  useEffect(() => {
    if (show && deploymentId) {
      loadDeploymentDetails();
      loadBillingSummary();
    }
  }, [show, deploymentId]);

  const loadDeploymentDetails = async () => {
    try {
      setLoading(true);
      const response = await getAgentDeployment(deploymentId);
      const formattedDeployment = formatDeploymentData(response.data);
      setDeployment(formattedDeployment);
    } catch (error) {
      console.error("Erro ao carregar detalhes:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível carregar os detalhes da implementação.",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadBillingSummary = async () => {
    try {
      setBillingLoading(true);
      const response = await getBillingSummary(deploymentId);
      setBillingSummary(response.data);
    } catch (error) {
      console.error("Erro ao carregar resumo de faturamento:", error);
      setBillingSummary(null);
    } finally {
      setBillingLoading(false);
    }
  };

  // Ações de status
  const handleStatusAction = async (action) => {
    if (!deployment) return;

    const actionMap = {
      activate: { fn: activateAgentDeployment, text: "ativar", color: "success" },
      suspend: { fn: suspendAgentDeployment, text: "suspender", color: "warning" },
      cancel: { fn: cancelAgentDeployment, text: "cancelar", color: "danger" },
    };

    const actionConfig = actionMap[action];
    if (!actionConfig) return;

    // Verificar permissões
    if (!canPerformAction(action, { user, isOrganization }, deployment)) {
      Swal.fire({
        icon: "warning",
        title: "Sem Permissão",
        text: "Você não tem permissão para executar esta ação.",
      });
      return;
    }

    // Confirmação
    const result = await Swal.fire({
      title: `Confirmar ${actionConfig.text}`,
      text: `Deseja ${actionConfig.text} esta implementação?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Sim, ${actionConfig.text}`,
      cancelButtonText: "Cancelar",
      confirmButtonColor: actionConfig.color === "success" ? "#28a745" : actionConfig.color === "warning" ? "#ffc107" : "#dc3545",
    });

    if (!result.isConfirmed) return;

    try {
      setActionLoading(prev => ({ ...prev, [action]: true }));
      
      await actionConfig.fn(deploymentId);
      
      Swal.fire({
        icon: "success",
        title: "Sucesso!",
        text: `Implementação ${actionConfig.text === "ativar" ? "ativada" : actionConfig.text === "suspender" ? "suspensa" : "cancelada"} com sucesso.`,
        timer: 2000,
        showConfirmButton: false,
      });

      // Recarregar detalhes e notificar componente pai
      loadDeploymentDetails();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error(`Erro ao ${actionConfig.text}:`, error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: `Não foi possível ${actionConfig.text} a implementação.`,
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setActionLoading(prev => ({ ...prev, [action]: false }));
    }
  };

  // Marcar setup como pago
  const handleMarkSetupPaid = async () => {
    if (!canPerformAction("financial", { user, isOrganization }, deployment)) {
      Swal.fire({
        icon: "warning",
        title: "Sem Permissão",
        text: "Você não tem permissão para esta ação financeira.",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Marcar Setup como Pago",
      text: "Confirma que a taxa de setup foi paga?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, marcar como pago",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#28a745",
    });

    if (!result.isConfirmed) return;

    try {
      setActionLoading(prev => ({ ...prev, setupPaid: true }));
      
      await markSetupFeePaid(deploymentId);
      
      Swal.fire({
        icon: "success",
        title: "Sucesso!",
        text: "Taxa de setup marcada como paga.",
        timer: 2000,
        showConfirmButton: false,
      });

      loadDeploymentDetails();
      loadBillingSummary();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Erro ao marcar setup como pago:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível marcar a taxa como paga.",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setActionLoading(prev => ({ ...prev, setupPaid: false }));
    }
  };

  // Processar cobrança
  const handleProcessBilling = async () => {
    if (!canPerformAction("financial", { user, isOrganization }, deployment)) {
      Swal.fire({
        icon: "warning",
        title: "Sem Permissão",
        text: "Você não tem permissão para esta ação financeira.",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Processar Cobrança",
      text: "Deseja processar a cobrança desta implementação?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, processar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#007bff",
    });

    if (!result.isConfirmed) return;

    try {
      setActionLoading(prev => ({ ...prev, billing: true }));
      
      await processBilling(deploymentId);
      
      Swal.fire({
        icon: "success",
        title: "Sucesso!",
        text: "Cobrança processada com sucesso.",
        timer: 2000,
        showConfirmButton: false,
      });

      loadBillingSummary();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Erro ao processar cobrança:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: error.response?.data?.detail || "Não foi possível processar a cobrança.",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setActionLoading(prev => ({ ...prev, billing: false }));
    }
  };

  // Estender suporte
  const handleExtendSupport = async () => {
    if (!canPerformAction("support", { user, isOrganization }, deployment)) {
      Swal.fire({
        icon: "warning",
        title: "Sem Permissão",
        text: "Você não tem permissão para gerenciar suporte.",
      });
      return;
    }

    if (!supportExtensionData.months || supportExtensionData.months < 1) {
      Swal.fire({
        icon: "warning",
        title: "Dados Inválidos",
        text: "Informe a quantidade de meses para extensão.",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Estender Suporte",
      text: `Deseja estender o suporte por ${supportExtensionData.months} mês(es)?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, estender",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#17a2b8",
    });

    if (!result.isConfirmed) return;

    try {
      setSupportLoading(true);
      
      await extendSupport(deploymentId, supportExtensionData);
      
      Swal.fire({
        icon: "success",
        title: "Sucesso!",
        text: "Suporte estendido com sucesso.",
        timer: 2000,
        showConfirmButton: false,
      });

      loadDeploymentDetails();
      setSupportExtensionData({ months: 1, reason: "" });
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Erro ao estender suporte:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: error.response?.data?.detail || "Não foi possível estender o suporte.",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setSupportLoading(false);
    }
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          <IconRocket size={24} className="me-2" />
          Detalhes da Implementação
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Carregando detalhes...</p>
          </div>
        ) : deployment ? (
          <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
            {/* Tab: Informações Gerais */}
            <Tab eventKey="general" title="📋 Informações Gerais">
              <Row>
                <Col md={6}>
                  <Card className="mb-3">
                    <Card.Header className="d-flex align-items-center">
                      <IconRocket size={18} className="me-2" />
                      <strong>Agente</strong>
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-2">
                        <strong>Nome:</strong> {deployment.agent?.name || "—"}
                      </div>
                      <div className="mb-2">
                        <strong>Área:</strong> {deployment.agent?.area || "—"}
                      </div>
                      <div className="mb-2">
                        <strong>Versão:</strong> {deployment.agent?.version || "—"}
                      </div>
                      <div>
                        <strong>Status do Agente:</strong>{" "}
                        <Badge bg={deployment.agent?.is_active ? "success" : "secondary"}>
                          {deployment.agent?.is_active ? "Ativo" : "Inativo"}
                        </Badge>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="mb-3">
                    <Card.Header className="d-flex align-items-center">
                      <IconBuilding size={18} className="me-2" />
                      <strong>Cliente</strong>
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-2">
                        <strong>Empresa:</strong> {deployment.subaccount?.company_name || "—"}
                      </div>
                    
                    
                      <div>
                        <strong>Status:</strong>{" "}
                        <Badge bg={deployment.subaccount?.is_active ? "success" : "secondary"}>
                          {deployment.subaccount?.is_active ? "Ativa" : "Inativa"}
                        </Badge>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col xs={12}>
                  <Card className="mb-3">
                    <Card.Header className="d-flex align-items-center">
                      <IconCash size={18} className="me-2" />
                      <strong>Valores e Suporte</strong>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={3}>
                          <div className="mb-2">
                            <strong>Taxa Setup:</strong>
                          </div>
                          <div className="fs-5 fw-bold text-primary">
                            {deployment.formattedSetupFee}
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="mb-2">
                            <strong>Mensalidade:</strong>
                          </div>
                          <div className="fs-5 fw-bold text-success">
                            {deployment.formattedMonthlyFee}
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="mb-2">
                            <strong>Tipo de Suporte:</strong>
                          </div>
                          <Badge bg={deployment.supportTypeVariant} className="fs-6">
                            {deployment.supportTypeText}
                          </Badge>
                        </Col>
                        <Col md={3}>
                          <div className="mb-2">
                            <strong>Data de Ativação:</strong>
                          </div>
                          <div>
                            <IconCalendar size={16} className="me-1" />
                            {deployment.formattedDeployedAt}
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>

            {/* Tab: Status & Ações */}
            <Tab eventKey="status" title="⚡ Status & Ações">
              <Card className="mb-3">
                <Card.Header className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <IconBolt size={18} className="me-2" />
                    <strong>Status Atual & Ações Rápidas</strong>
                  </div>
                  <Badge bg={deployment.statusVariant} className="fs-6">
                    {deployment.statusText}
                  </Badge>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <strong>Status da Implementação:</strong>
                        <div className="mt-2">
                          <Badge bg={deployment.statusVariant} className="fs-6 me-2">
                            {deployment.statusText}
                          </Badge>
                          {deployment.status === "active" && <IconCheck size={16} className="text-success" />}
                          {deployment.status === "suspended" && <IconPlayerPause size={16} className="text-warning" />}
                          {deployment.status === "canceled" && <IconX size={16} className="text-danger" />}
                        </div>
                      </div>

                      <div className="mb-3">
                        <strong>Setup Fee:</strong>
                        <div className="mt-2">
                          <Badge bg={deployment.setupFeePaidVariant} className="fs-6">
                            {deployment.setupFeePaidText}
                          </Badge>
                        </div>
                      </div>

                      {deployment.deployed_at && (
                        <div>
                          <strong>Última Atualização:</strong>
                          <div className="mt-1 text-muted">
                            <IconClock size={16} className="me-1" />
                            {format(new Date(deployment.deployed_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                          </div>
                        </div>
                      )}
                    </Col>

                    <Col md={6}>
                      <strong>Ações Disponíveis:</strong>
                      <div className="mt-3 d-grid gap-2">
                        {/* Ativar */}
                        {deployment.status !== "active" && (
                          <Button
                            variant="success"
                            onClick={() => handleStatusAction("activate")}
                            disabled={actionLoading.activate || !canPerformAction("activate", { user, isOrganization }, deployment)}
                          >
                            {actionLoading.activate ? <Spinner size="sm" className="me-2" /> : <IconBolt size={16} className="me-2" />}
                            Ativar Implementação
                          </Button>
                        )}

                        {/* Suspender */}
                        {deployment.status === "active" && (
                          <Button
                            variant="warning"
                            onClick={() => handleStatusAction("suspend")}
                            disabled={actionLoading.suspend || !canPerformAction("suspend", { user, isOrganization }, deployment)}
                          >
                            {actionLoading.suspend ? <Spinner size="sm" className="me-2" /> : <IconPlayerPause size={16} className="me-2" />}
                            Suspender Implementação
                          </Button>
                        )}

                        {/* Cancelar */}
                        {deployment.status !== "canceled" && (
                          <Button
                            variant="danger"
                            onClick={() => handleStatusAction("cancel")}
                            disabled={actionLoading.cancel || !canPerformAction("cancel", { user, isOrganization }, deployment)}
                          >
                            {actionLoading.cancel ? <Spinner size="sm" className="me-2" /> : <IconX size={16} className="me-2" />}
                            Cancelar Implementação
                          </Button>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Tab>

            {/* Tab: Financeiro */}
            <Tab eventKey="financial" title="💰 Financeiro">
              <Row>
                <Col md={8}>
                  <Card className="mb-3">
                    <Card.Header className="d-flex align-items-center">
                      <IconCreditCard size={18} className="me-2" />
                      <strong>Resumo Financeiro</strong>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="ms-auto"
                        onClick={loadBillingSummary}
                        disabled={billingLoading}
                      >
                        {billingLoading ? <Spinner size="sm" /> : <IconRefresh size={14} />}
                      </Button>
                    </Card.Header>
                    <Card.Body>
                      {billingLoading ? (
                        <div className="text-center py-3">
                          <Spinner animation="border" size="sm" />
                          <span className="ms-2">Carregando resumo...</span>
                        </div>
                      ) : billingSummary ? (
                        <Row>
                          <Col md={4}>
                            <div className="mb-3">
                              <strong>Total Faturado:</strong>
                              <div className="fs-4 fw-bold text-success">
                                {formatCurrency(billingSummary.total_billed)}
                              </div>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="mb-3">
                              <strong>Pendente:</strong>
                              <div className="fs-4 fw-bold text-warning">
                                {formatCurrency(billingSummary.pending_amount)}
                              </div>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="mb-3">
                              <strong>Próxima Cobrança:</strong>
                              <div className="text-muted">
                                {billingSummary.next_billing_date 
                                  ? format(new Date(billingSummary.next_billing_date), "dd/MM/yyyy", { locale: ptBR })
                                  : "—"}
                              </div>
                            </div>
                          </Col>
                        </Row>
                      ) : (
                        <Alert variant="info">
                          Nenhum dado de faturamento disponível.
                        </Alert>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={4}>
                  <Card>
                    <Card.Header className="d-flex align-items-center">
                      <IconCash size={18} className="me-2" />
                      <strong>Ações Financeiras</strong>
                    </Card.Header>
                    <Card.Body>
                      <div className="d-grid gap-2">
                        {!deployment.is_setup_fee_paid && (
                          <Button
                            variant="success"
                            onClick={handleMarkSetupPaid}
                            disabled={actionLoading.setupPaid || !canPerformAction("financial", { user, isOrganization }, deployment)}
                          >
                            {actionLoading.setupPaid ? <Spinner size="sm" className="me-2" /> : <IconCheck size={16} className="me-2" />}
                            Marcar Setup como Pago
                          </Button>
                        )}
{/* 
                        <Button
                          variant="primary"
                          onClick={handleProcessBilling}
                          disabled={actionLoading.billing || !canPerformAction("financial", { user, isOrganization }, deployment)}
                        >
                          {actionLoading.billing ? <Spinner size="sm" className="me-2" /> : <IconCreditCard size={16} className="me-2" />}
                          Processar Cobrança
                        </Button> */}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>

            {/* Tab: Suporte */}
            <Tab eventKey="support" title="🎧 Suporte">
              <Row>
                <Col md={8}>
                  <Card className="mb-3">
                    <Card.Header className="d-flex align-items-center">
                      <IconHeadset size={18} className="me-2" />
                      <strong>Informações de Suporte</strong>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={4}>
                          <div className="mb-3">
                            <strong>Tipo de Suporte:</strong>
                            <div className="mt-2">
                              <Badge bg={deployment.supportTypeVariant} className="fs-6">
                                {deployment.supportTypeText}
                              </Badge>
                            </div>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="mb-3">
                            <strong>Data de Início:</strong>
                            <div className="mt-1">
                              {deployment.support_start_date 
                                ? format(new Date(deployment.support_start_date), "dd/MM/yyyy", { locale: ptBR })
                                : deployment.formattedDeployedAt}
                            </div>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="mb-3">
                            <strong>Data de Expiração:</strong>
                            <div className="mt-1">
                              {deployment.support_end_date 
                                ? format(new Date(deployment.support_end_date), "dd/MM/yyyy", { locale: ptBR })
                                : "—"}
                            </div>
                          </div>
                        </Col>
                      </Row>

                      {deployment.support_notes && (
                        <div>
                          <strong>Observações de Suporte:</strong>
                          <div className="mt-2 p-3 bg-light rounded">
                            {deployment.support_notes}
                          </div>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={4}>
                  <Card>
                    <Card.Header className="d-flex align-items-center">
                      <IconPlus size={18} className="me-2" />
                      <strong>Estender Suporte</strong>
                    </Card.Header>
                    <Card.Body>
                      <Form>
                        <div className="mb-3">
                          <Form.Label>Quantidade de Meses:</Form.Label>
                          <Form.Control
                            type="number"
                            min="1"
                            max="12"
                            value={supportExtensionData.months}
                            onChange={(e) => setSupportExtensionData(prev => ({
                              ...prev,
                              months: parseInt(e.target.value) || 1
                            }))}
                          />
                        </div>

                        <div className="mb-3">
                          <Form.Label>Motivo (Opcional):</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={supportExtensionData.reason}
                            onChange={(e) => setSupportExtensionData(prev => ({
                              ...prev,
                              reason: e.target.value
                            }))}
                            placeholder="Descreva o motivo da extensão..."
                          />
                        </div>

                        <Button
                          variant="info"
                          onClick={handleExtendSupport}
                          disabled={supportLoading || !canPerformAction("support", { user, isOrganization }, deployment)}
                          className="w-100"
                        >
                          {supportLoading ? <Spinner size="sm" className="me-2" /> : <IconPlus size={16} className="me-2" />}
                          Estender Suporte
                        </Button>
                      </Form>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>
          </Tabs>
        ) : (
          <Alert variant="warning">
            Não foi possível carregar os detalhes da implementação.
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AgentDeploymentModal;